import { computed, onMounted, readonly, ref, unref } from "vue";
import { useRouter } from "vue-router/dist/composables.js";
import { isEqual } from "lodash";
import { useNotify } from "@/composables/useNotify";
import { useRoute } from "@/composables/useRoute";
import { extractErrorMessage } from "@/helpers/functions/errorHelpers";
import { deepCopy } from "@/helpers/functions/ObjectHelpers";
import { stripAccents } from "@/helpers/functions/StringHelpers";

/**
 * @typedef {Object} UseTableListOptions
 * @property {import("vue").Ref<Object>} pagination
 * @property {import("vue").Ref<Object>} [params={}]
 * @property {(payload: Object) => Promise<unknown>} fetchItems
 * @property {(id: number|string) => Promise<unknown>} removeItem
 * @property {(pagination: Object) => Promise<unknown>} [setPagination]
 * @property {string} [searchParam="q"]
 * @property {string} [defaultSortBy="id"]
 * @property {number} [defaultPerPage=15]
 */

/**
 * Reúne o estado e as ações compartilhadas pelas tabelas paginadas.
 *
 * @param {UseTableListOptions} options
 */
export function useTableList({
  pagination,
  params,
  fetchItems,
  removeItem,
  setPagination,
  searchParam = "q",
  defaultSortBy = "id",
  defaultPerPage = 15,
}) {
  const route = useRoute();
  const router = useRouter();
  const $notifications = useNotify();

  const searchText = ref("");
  const sortBy = ref(defaultSortBy);
  const sortDesc = ref(false);
  const isBusy = ref(false);

  let lastRequest = null;

  const displayReset = computed(
    () => stripAccents(searchText.value).length > 0
  );

  function getPagination() {
    return unref(pagination) || {};
  }

  function getParams() {
    const currentParams = deepCopy(unref(params) || {});

    return Object.entries(currentParams).reduce((result, [key, value]) => {
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isNullish = value === null || value === undefined;

      if (!isNullish && !isEmptyArray) result[key] = value;

      return result;
    }, {});
  }

  async function updatePagination(patch) {
    const nextPagination = {
      ...getPagination(),
      ...patch,
    };

    if (setPagination) {
      await setPagination(nextPagination);
      return;
    }

    Object.assign(getPagination(), patch);
  }

  function reset() {
    searchText.value = "";
    sortBy.value = defaultSortBy;
    sortDesc.value = false;

    loadData(true);
  }

  async function loadData(forceRefetch = false) {
    const currentPagination = getPagination();
    const payload = {
      page: currentPagination.current_page || 1,
      per_page: currentPagination.per_page || defaultPerPage,
      orderBy: sortBy.value,
      sortBy: sortDesc.value ? "desc" : "asc",
      [searchParam]: searchText.value,
      ...getParams(),
    };

    if (!forceRefetch && isEqual(lastRequest, payload)) return;

    lastRequest = payload;
    isBusy.value = true;

    try {
      await fetchItems(payload);
    } catch (error) {
      if (isEqual(lastRequest, payload)) lastRequest = null;

      $notifications?.addNotification({
        message: "Erro ao carregar dados da listagem",
        icon: "exclamation-triangle",
        type: "danger",
      });
    } finally {
      isBusy.value = false;
    }
  }

  async function searchTerm() {
    await updatePagination({ current_page: 1 });

    const query = { ...route.query };
    delete query.page;

    if (searchText.value) {
      query[searchParam] = searchText.value;
    } else {
      delete query[searchParam];
    }

    if (!isEqual(query, route.query)) {
      await router.push({ query });
    }

    await loadData();
  }

  async function sortingChanged({ sortBy: field, sortDesc: descending }) {
    sortBy.value = field;
    sortDesc.value = descending;

    await loadData();
  }

  async function deleteItem(item) {
    try {
      await removeItem(item.id);

      $notifications?.addNotification({
        message: "Registro deletado com sucesso",
        type: "success",
      });

      await loadData(true);
    } catch (error) {
      $notifications?.addNotification({
        message: extractErrorMessage(error, "Erro ao excluir registro"),
        type: "danger",
      });
    }
  }

  async function initialize() {
    const routePage = Number.parseInt(route.query.page, 10);

    searchText.value = String(route.query[searchParam] || "");

    await updatePagination({
      current_page: routePage > 0 ? routePage : 1,
      per_page: getPagination().per_page || defaultPerPage,
    });

    await loadData();
  }

  onMounted(initialize);

  return {
    termo_pesquisa: searchText,
    sortBy,
    sortDesc,
    isBusy: readonly(isBusy),
    displayReset,
    searchTerm,
    sortingChanged,
    loadData,
    deleteItem,
    reset,
  };
}
